/**
 * Acciones humanas sobre una conversación.
 *
 * Cableado real para `take` y `release` (endpoints `/api/moderation/{takeover,release}`).
 *
 * `escalate` y `close` no tienen endpoint todavía — devolvemos un error
 * explícito en el toast para que el operador sepa que la acción no fue
 * persistida, en lugar de mentir con un mock.
 *
 * El backend acepta `messageId` (int) en lugar de `conversationId`. El caller
 * lo deriva del último mensaje entrante del thread.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiPost } from "@/lib/api";

type Action = "take" | "release" | "escalate" | "close" | "mark_external";

interface ActionPayload {
  /** ID numérico del último mensaje entrante (cm_incoming_messages.id). */
  messageId: number;
  action: Action;
  /** Solo aplica a release / close. */
  internalNote?: string;
  /** Solo aplica a take. Default 60. */
  durationMinutes?: number;
  /** Solo aplica a escalate. */
  category?: string;
  /** Solo aplica a escalate. */
  reason?: string;
  /** Solo aplica a mark_external. Nota corta sobre dónde se respondió. */
  externalNote?: string;
}

interface TakeoverResponse {
  ok: boolean;
  takeover?: unknown;
}

const ACTION_LABEL: Record<Action, string> = {
  take: "tomaste la conversación",
  release: "devolviste al bot",
  escalate: "escalada",
  close: "cerrada",
  mark_external: "marcada como respondida por fuera",
};

export function useConversationAction() {
  const qc = useQueryClient();

  return useMutation<TakeoverResponse, Error, ActionPayload>({
    mutationFn: async ({
      messageId,
      action,
      internalNote,
      durationMinutes,
      category,
      reason,
      externalNote,
    }) => {
      if (action === "take") {
        return apiPost<TakeoverResponse>("/moderation/takeover", {
          messageId,
          durationMinutes: durationMinutes ?? 60,
        });
      }
      if (action === "release") {
        return apiPost<TakeoverResponse>("/moderation/release", {
          messageId,
          ...(internalNote ? { internalNote } : {}),
        });
      }
      if (action === "escalate") {
        return apiPost<TakeoverResponse>("/moderation/escalate", {
          messageId,
          category: category ?? "other",
          reason: reason ?? "Sin razón",
        });
      }
      if (action === "close") {
        return apiPost<TakeoverResponse>("/moderation/close", {
          messageId,
          ...(internalNote ? { reason: internalNote } : {}),
        });
      }
      if (action === "mark_external") {
        return apiPost<TakeoverResponse>("/moderation/mark-external-response", {
          messageId,
          ...(externalNote ? { note: externalNote } : {}),
        });
      }
      throw new Error(`acción desconocida: ${action}`);
    },
    onSuccess: (_data, { action }) => {
      // OJO: el queryKey real del detalle es ["conversation-detail", id],
      // NO ["conversation"] ni ["messages"]. Invalidar las claves viejas
      // no matcheaba nada y por eso el operador tenía que recargar la
      // página después de Tomar control / Devolver al bot.
      qc.invalidateQueries({ queryKey: ["conversations"] });
      qc.invalidateQueries({ queryKey: ["conversation-detail"] });
      toast.success(ACTION_LABEL[action]);
    },
    onError: (err) => {
      toast.error(`error: ${err.message}`);
    },
  });
}
